import { useMemo, useRef, FC } from 'react';
import { useAtomValue } from 'jotai';
import FilterChipCarousel from 'components/ui/FilterChipCarousel/FilterChipCarousel';
import { DSUQueryNameAtom, nearParamAtom, areaParamAtom } from '../Filter/filterStore';
import { FilterPresetsProps, LocationData } from './types';
import { getPresetsForLocation } from './helpers';
import useScrollVisibility from './hooks/useScrollVisibility';
import useFilterPresetShowTracking from './hooks/useFilterPresetShowTracking';
import { getLocationName, mapPresetsToChipItems, normalizeString } from './utils';
import usePresetHandleClick from './hooks/usePresetHandleClick';

const useLocationData = (): LocationData => ({
  DSUQueryName: useAtomValue(DSUQueryNameAtom),
  near: useAtomValue(nearParamAtom),
  area: useAtomValue(areaParamAtom),
});

// Main component
const FilterPresets: FC<FilterPresetsProps> = ({ onPresetApplied, ...props }) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const locationData = useLocationData();
  const { selectedPreset, handlePresetClick } = usePresetHandleClick(onPresetApplied);

  // Get current location name and normalize it (lowercased)
  const locationName = useMemo(() => getLocationName(locationData), [locationData]);
  const normalizedLocation = useMemo(() => normalizeString(locationName), [locationName]);

  // return visible elements on viewport
  const { visibleElements, visibilityEventType, scrollDirection } = useScrollVisibility(
    carouselRef,
    normalizedLocation
  );

  // Get presets for the current location
  const locationPresets = useMemo(() => {
    if (!normalizedLocation) return [];
    if (selectedPreset) return [];
    return getPresetsForLocation(normalizedLocation);
  }, [normalizedLocation, selectedPreset]);

  const presetItems = useMemo(
    () => mapPresetsToChipItems(locationPresets, selectedPreset),
    [locationPresets, selectedPreset]
  );

  // tracking hook for show and scroll events
  useFilterPresetShowTracking({ visibleElements, visibilityEventType, locationPresets, scrollDirection });

  // Early return for empty state
  if (locationPresets?.length === 0) {
    return null;
  }

  return (
    <FilterChipCarousel
      ref={carouselRef}
      items={presetItems}
      onItemClick={handlePresetClick}
      testIdPrefix="filter-preset"
      chipVariant="default"
      allowMultipleSelection={false}
      {...props}
    />
  );
};

export default FilterPresets;
